import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Download, Sparkles } from 'lucide-react';
import QuotaWarning from '@/components/QuotaWarning';
import type { QuotaPressure } from '@/lib/quota/deriveQuotaPressure';
import { resolveExportFormat } from '@/lib/export/resolveExportFormat';

type ExportFormat = 'vertical' | 'horizontal';

interface Props {
  topBarRef: React.Ref<HTMLDivElement>;
  projectName: string;

  /* status slot */
  hasClipQuota: boolean;
  quotaPressure?: QuotaPressure | null;
  autoSaving: boolean;

  /* actions */
  canClipVideos: boolean;
  /** Opens this clip in AI Studio; omitted when the viewer lacks the assistant. */
  onOpenAiStudio?: () => void;

  /* export gating */
  checkedCount: number;
  hasProFeatures: boolean;
  canExportCheckedClipsLayers: boolean;
  canExportSelectedClipLayers: boolean;
  onBulkProGate: () => void;
  onQuotaBlocked: () => void;
  onLayersProGate: () => void;

  /* export actions */
  availableFormats: ExportFormat[];
  effectivePreviewAspect: 'landscape' | 'portrait';
  onOpenMassExportModal: () => void;
  onSingleExport: (format: ExportFormat) => void;

  /* export button state */
  isMassExporting: boolean;
  isExporting: boolean;
  createClipLoading: boolean;
  activateClipLoading: boolean;
  isInvalidDuration: boolean;
  durationErrorMessage: string;
}

/** The editor's sticky top bar: project title, the single status slot, and
 *  the Edit Video / Export button cluster. Extracted verbatim from
 *  ProjectEditor — gate DECISIONS stay with the buttons; what a gate does
 *  (dialog, toast) is the parent's, passed as callbacks. The
 *  `tour-export-button` classes and `export-trigger` testid are load-bearing
 *  (page tour + Cypress). */
export const EditorTopBar: React.FC<Props> = ({
  topBarRef, projectName,
  hasClipQuota, quotaPressure = null, autoSaving,
  canClipVideos, onOpenAiStudio,
  checkedCount, hasProFeatures,
  canExportCheckedClipsLayers, canExportSelectedClipLayers,
  onBulkProGate, onQuotaBlocked, onLayersProGate,
  availableFormats, effectivePreviewAspect,
  onOpenMassExportModal, onSingleExport,
  isMassExporting, isExporting, createClipLoading, activateClipLoading,
  isInvalidDuration, durationErrorMessage,
}) => {
  const { t } = useTranslation();
  return (
    <div ref={topBarRef} className="sticky top-[0px] z-10 bg-background p-3 sm:p-4 lg:p-6 border-b border-border">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
        <div className="tour-editor-title">
          <h1 className="text-lg sm:text-lg lg:text-xl font-bold">
            {projectName}
          </h1>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:gap-4">
            {/* ONE status at a time, by priority: the quota block (which
                changes what the buttons do) outranks the transient saving
                indicator. Scattered simultaneous statuses read as noise. */}
            {!hasClipQuota || quotaPressure ? (
              <QuotaWarning hasClipQuota={hasClipQuota} pressure={quotaPressure} />
            ) : autoSaving ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <div className="h-3 w-3 animate-spin rounded-full border border-primary border-t-transparent" />
                {t('projectEditor.saving')}
              </span>
            ) : null}
            {canClipVideos && (
              <>
                {onOpenAiStudio && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onOpenAiStudio}
                    className="flex w-full sm:w-auto"
                    data-testid="editor-open-ai-studio"
                  >
                    <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                    <span>{t('aiAssistant.studio.open')}</span>
                  </Button>
                )}
                {checkedCount >= 2 ? (
                  <Button
                    variant="gradient"
                    size="sm"
                    onClick={() => {
                      if (!hasProFeatures) {
                        onBulkProGate();
                        return;
                      }
                      if (!hasClipQuota) {
                        onQuotaBlocked();
                        return;
                      }
                      if (!canExportCheckedClipsLayers) {
                        onLayersProGate();
                        return;
                      }
                      onOpenMassExportModal();
                    }}
                    disabled={isMassExporting}
                    className="tour-export-button"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="sm:inline">
                      {isMassExporting
                        ? t('projectEditor.massExporting')
                        : t('projectEditor.exportNClips', { count: checkedCount })}
                    </span>
                  </Button>
                ) : (
                  <Button
                    data-testid="export-trigger"
                    variant="gradient"
                    size="sm"
                    onClick={() => {
                      if (!hasClipQuota) {
                        onQuotaBlocked();
                        return;
                      }
                      if (!canExportSelectedClipLayers) {
                        onLayersProGate();
                        return;
                      }
                      // Export what the user is looking at — never ask.
                      onSingleExport(
                        resolveExportFormat({
                          previewAspect: effectivePreviewAspect,
                          availableFormats,
                        }),
                      );
                    }}
                    className="tour-export-button"
                    disabled={
                      isExporting ||
                      createClipLoading ||
                      activateClipLoading ||
                      isInvalidDuration
                    }
                    title={durationErrorMessage}
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="sm:inline">
                      {isExporting || createClipLoading || activateClipLoading
                        ? t('projectEditor.exporting')
                        : t('projectEditor.exportVideo')}
                    </span>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorTopBar;
